import { ContractDetailsModel } from '@/modules/drivers/types/contracts'
import { Checkbox } from '@/ui'
import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface Props {
  data: ContractDetailsModel | null
  setData: React.Dispatch<React.SetStateAction<ContractDetailsModel | null>>
  reduceType: 'rate' | 'additional'
  disabled?: boolean
  background?: boolean
  padding?: boolean
  defaultCheck?: boolean
  mainCheckboxProperty?: keyof ContractDetailsModel
  mainLabel?: string
  sickLeaveLabel?: string
  paidLeaveLabel?: string
  unpaidLeaveLabel?: string
  sickLeaveProperty?: keyof ContractDetailsModel
  paidLeaveProperty?: keyof ContractDetailsModel
  unpaidLeaveProperty?: keyof ContractDetailsModel
}

const RateReduceCustom = ({
  data,
  setData,
  reduceType,
  defaultCheck,
  disabled = false,
  background = false,
  padding = true,
  mainCheckboxProperty,
  mainLabel,
  sickLeaveLabel,
  paidLeaveLabel,
  unpaidLeaveLabel,
  sickLeaveProperty,
  paidLeaveProperty,
  unpaidLeaveProperty,
}: Props) => {
  const { t } = useLocalize()
  const reduce = mainCheckboxProperty
    ? (data?.[mainCheckboxProperty] as boolean) || false
    : defaultCheck

  const labels =
    reduceType === 'rate'
      ? {
          sickLeave: sickLeaveLabel || t('contract.reduce-sick-leave-rate'),
          paidLeave: paidLeaveLabel || t('contract.reduce-paid-leave-rate'),
          unpaidLeave:
            unpaidLeaveLabel || t('contract.reduce-unpaid-leave-rate'),
        }
      : {
          sickLeave:
            sickLeaveLabel || t('contract.reduce-sick-leave-additional'),
          paidLeave:
            paidLeaveLabel || t('contract.reduce-paid-leave-additional'),
          unpaidLeave:
            unpaidLeaveLabel || t('contract.reduce-unpaid-leave-additional'),
        }

  const properties =
    reduceType === 'rate'
      ? {
          sickLeave: (sickLeaveProperty ||
            'reduceRateOfAbsenceForSickLeave') as keyof ContractDetailsModel,
          paidLeave: (paidLeaveProperty ||
            'reduceRateOfAbsenceForLeave') as keyof ContractDetailsModel,
          unpaidLeave: (unpaidLeaveProperty ||
            'reduceRateOfAbsenceForNotPaidLeave') as keyof ContractDetailsModel,
        }
      : {
          sickLeave: (sickLeaveProperty ||
            'reduceAdditionalRatesForSickLeave') as keyof ContractDetailsModel,
          paidLeave: (paidLeaveProperty ||
            'reduceAdditionalRatesForLeave') as keyof ContractDetailsModel,
          unpaidLeave: (unpaidLeaveProperty ||
            'reduceAdditionalRatesForNotPaidLeave') as keyof ContractDetailsModel,
        }

  return (
    <div
      className={`tw-w-full tw-space-y-3 tw-rounded-md ${padding ? 'tw-p-3' : ''} ${
        background ? 'tw-bg-[#f2f2f2]/50' : ''
      } ${disabled ? 'tw-opacity-50' : ''}`}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <Checkbox
          checked={reduce}
          onChange={(e) => {
            if (mainCheckboxProperty) {
              const updates: Partial<ContractDetailsModel> = {
                [mainCheckboxProperty]: e.target.checked,
                [properties.sickLeave]: e.target.checked,
                [properties.paidLeave]: e.target.checked,
                [properties.unpaidLeave]: e.target.checked,
              }
              setData({ ...data, ...updates })
            }
          }}
          disabled={!mainCheckboxProperty}
        />
        <p className="tw-text-sm tw-text-content-base">
          {mainLabel || t('contract.reduce-on-absence')}
        </p>
      </div>

      {reduce && (
        <div className="tw-space-y-3 tw-px-5">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              checked={(data?.[properties.sickLeave] as boolean) || false}
              onChange={(e) =>
                setData({
                  ...data,
                  [properties.sickLeave]: e.target.checked,
                })
              }
              disabled={disabled}
            />
            <p className="tw-text-sm tw-text-content-base">
              {labels.sickLeave}
            </p>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              checked={(data?.[properties.paidLeave] as boolean) || false}
              onChange={(e) =>
                setData({
                  ...data,
                  [properties.paidLeave]: e.target.checked,
                })
              }
              disabled={disabled}
            />
            <p className="tw-text-sm tw-text-content-base">
              {labels.paidLeave}
            </p>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              checked={(data?.[properties.unpaidLeave] as boolean) || false}
              onChange={(e) =>
                setData({
                  ...data,
                  [properties.unpaidLeave]: e.target.checked,
                })
              }
              disabled={disabled}
            />
            <p className="tw-text-sm tw-text-content-base">
              {labels.unpaidLeave}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default RateReduceCustom
